import 'suneditor/dist/css/suneditor.min.css';

import { Add } from '@mui/icons-material';
import {
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Grid,
    MenuItem,
    Stack,
} from '@mui/material';

import ActionButton from 'common/components/ActionButton';
import ImageContainer from 'common/components/ImageContainer';
import useStylesForm from 'common/components/styles/useStylesForm.styles';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useDispatch, useSelector } from 'react-redux';
import { deleteAsset, getAssets, getAssetsUploading, setAssets, uploadAssetAction } from 'redux/slices/assetSlice';

import CustomSelectValidator from 'common/components/CustomSelectValidators';
import CustomSunEditor from 'common/components/CustomSunEditor';
import CustomTextValidator from 'common/components/CustomTextValidators';
import ImageFileInput from 'common/components/ImageFileInput';
import NumberFormatCustom from 'common/components/NumberFormatCustom';
import LabelsInput from 'common/features/labels/LabelsInput';
import RenderFormExtraFields from 'common/features/renderForms/RenderFormExtraFields';
import { useAbortController } from 'common/hooks';
import { getProductsSavePending } from 'redux/slices/productSlice';
import { useCurrencies } from '../../../common/constants/currencies';
import useStylesProductForm from '../styles/useStylesProductForm.styles';
import ProductFormCommon from './ProductFormCommon';

const discounts = [
    {
        name: 'product.productFormConfiguration.none',
        value: 'none',
    },
    {
        name: 'product.productFormConfiguration.discountPercent',
        value: 'percent',
    },
    {
        name: 'product.productFormConfiguration.discountAmount',
        value: 'amount',
    },
];

const ProductFormConfiguration = (props) => {
    const {
        fields,
        handleFieldChange,
        step,
        setStep,
        cancel,
        onSubmit,
        handleProductCurrencyFieldChange,
        productVariantFields,
        handleProductVariantFieldChange,
        handleDescriptionChange,
        descriptionFields,
        readOnly,
        canEdit,
        formConfigProduct,
    } = props;

    const { classes: classes1 } = useStylesForm();
    const { classes: classes2, cx } = useStylesProductForm();
    const classes = { ...classes1, ...classes2 };
    const savePending = useSelector(getProductsSavePending);
    const [currencies, currenciesHash] = useCurrencies();
    const dispatch = useDispatch();
    const [sunError, setSunError] = useState(false);

    const assets = useSelector(getAssets);
    const uploading = useSelector(getAssetsUploading);
    const getSignal = useAbortController();

    const [submitted, setSubmitted] = useState(false);

    const editorConfig = {
        buttonList: [
            readOnly
                ? ['fullScreen', 'codeView', 'preview']
                : [
                    'undo',
                    'redo',
                    'font',
                    'fontSize',
                    'formatBlock',
                    'paragraphStyle',
                    'blockquote',
                    'bold',
                    'underline',
                    'italic',
                    'strike',
                    'subscript',
                    'superscript',
                    'fontColor',
                    'hiliteColor',
                    'textStyle',
                    'removeFormat',
                    'outdent',
                    'indent',
                    'align',
                    'horizontalRule',
                    'list',
                    'lineHeight',
                    'link',
                    'fullScreen',
                    'showBlocks',
                    'codeView',
                    'preview',
                ],
        ],
        addTagsWhitelist: 'button',
        pasteTagsWhitelist: 'button',
        attributesWhitelist: {
            button: 'style|class|onclick|type|disabled|data-*',
            all: 'style|class|data-*',
        },
        formats: ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    };

    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const validateForm = () => {
        if (!assets || assets.length === 0) {
            enqueueSnackbar(t('product.productFormVariants.imageError'), {
                variant: 'error',
            });
            return false;
        }
        return true;
    };

    useEffect(() => {
        dispatch(setAssets(fields.images));
    }, [dispatch, fields.images]);

    function completeImagesItems() {
        const items = [];
        for (let i = 0; i < (readOnly ? 4 : 3) - assets.length; i++) {
            items.push(
                <Grid key={i} item md={3}>
                    <div className={classes.imagesComplement} />
                </Grid>,
            );
        }
        return items;
    }

    const handleTagChange = (e) => {
        handleFieldChange({
            target: {
                name: 'tags',
                value: e,
            },
        });
    };

    function handleUploadFile(file) {
        dispatch(uploadAssetAction(file, getSignal()));
    }

    const submit = () => {
        setSubmitted(true);
        if (!validateForm()) {
            return;
        }
        if (!readOnly) {
            if (fields.enableVariants) {
                handleFieldChange({
                    target: {
                        name: 'images',
                        value: assets,
                    },
                });
                setStep(step + 1);
            } else {
                onSubmit();
            }
        } else {
            setStep(step + 1);
        }
        // }
    };

    function handleError() {
        setSubmitted(true);
        enqueueSnackbar(t('product.productFormConfiguration.formError'), {
            variant: 'error',
        });
    }

    const onDelete = (e, idx) => {
        e.preventDefault();
        dispatch(deleteAsset(idx));
    };

    const descriptionChange = (value) => {
        handleDescriptionChange({
            target: {
                name: 'descriptionHtml',
                value: value,
            },
        });
    };

    const getButtonText = () => {
        if (readOnly) {
            return t('product.productFormConfiguration.continue');
        }
        return fields.enableVariants
            ? t('product.productFormConfiguration.saveContinue')
            : t('product.productFormConfiguration.save');
    };

    return (
        <ValidatorForm onSubmit={submit} onError={handleError} className={classes.productForm}>
            <div className={classes.formArea}>
                <div className={classes.scroll}>
                    <FormGroup className={classes.formGroup}>
                        <FormLabel>{t('product.productFormConfiguration.name')}</FormLabel>
                        <CustomTextValidator
                            InputProps={{
                                readOnly: readOnly,
                            }}
                            name="name"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            value={fields.name}
                            onChange={handleFieldChange}
                            validators={['required']}
                            errorMessages={[t('product.productFormConfiguration.nameRequired')]}
                        />
                    </FormGroup>
                    <FormGroup className={classes.formGroup}>
                        <FormLabel>{t('product.productFormConfiguration.externalId')}</FormLabel>
                        <CustomTextValidator
                            InputProps={{
                                readOnly: readOnly,
                            }}
                            name="externalId"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            value={fields.externalId}
                            onChange={handleFieldChange}
                        />
                    </FormGroup>

                    <FormGroup className={classes.formGroup}>
                        <FormLabel>SKU</FormLabel>
                        <CustomTextValidator
                            InputProps={{
                                readOnly: readOnly,
                            }}
                            name="sku"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            value={fields.sku}
                            onChange={handleFieldChange}
                        // validators={['required']}
                        // errorMessages={[t('product.productFormConfiguration.nameRequired')]}
                        />
                    </FormGroup>

                    <FormGroup className={classes.formGroup}>
                        <FormLabel>GTIN</FormLabel>
                        <CustomTextValidator
                            InputProps={{
                                readOnly: readOnly,
                            }}
                            name="gtin"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            value={fields.gtin}
                            onChange={handleFieldChange}
                        // validators={['required']}
                        // errorMessages={[t('product.productFormConfiguration.nameRequired')]}
                        />
                    </FormGroup>
                    <FormGroup className={classes.formGroup}>
                        <FormLabel>{t('product.productFormConfiguration.description')}</FormLabel>
                        <FormControl
                            className={
                                classes.formControl /*+ ' ' + (descriptionError ? classes.descriptionError : '')*/
                            }
                        >
                            <CustomSunEditor
                                readOnly={readOnly}
                                defaultValue={descriptionFields.descriptionHtml}
                                setOptions={editorConfig}
                                onChange={descriptionChange}
                                sunError={setSunError}
                            />
                        </FormControl>
                    </FormGroup>
                    <FormGroup className={classes.formGroup}>
                        <FormLabel>{t('product.productFormConfiguration.tags')}</FormLabel>
                        <FormControl className={classes.formControl}>
                            <LabelsInput
                                deleteAction={true}
                                namespace={'product'}
                                readOnly={readOnly}
                                labels={fields?.tags}
                                onChange={(e) => handleTagChange(e)}
                                placeholder={t('product.productFormConfiguration.tagsPlaceholder')}
                            />
                        </FormControl>
                    </FormGroup>

                    <FormGroup className={classes.formGroup}>
                        <FormLabel>{t('product.productFormConfiguration.image')}</FormLabel>
                        <Grid container spacing={1} className={classes.imagesContainer}>
                            {assets.map((asset, idx) => (
                                <ImageContainer
                                    key={idx}
                                    asset={asset}
                                    onDelete={
                                        readOnly
                                            ? (e) => {
                                                e.preventDefault();
                                            }
                                            : (e) => onDelete(e, idx)
                                    }
                                    deleteButton={!readOnly}
                                    size={'big'}
                                />
                            ))}
                            {!readOnly && assets.length < 4 && (
                                <Grid item md={3}>
                                    <ImageFileInput
                                        inputId={'raised-button-file'}
                                        uploadAssetFn={handleUploadFile}
                                        disabled={uploading}
                                    />
                                    <label htmlFor="raised-button-file">
                                        <Button
                                            component="span"
                                            className={cx(classes.uploadImageButton, classes.uploadImageButtonBig)}
                                        >
                                            {uploading && <CircularProgress />}
                                            {!uploading && (
                                                <>
                                                    <Add />
                                                    <span>{t('product.productFormConfiguration.upload')}</span>
                                                </>
                                            )}
                                        </Button>
                                    </label>
                                </Grid>
                            )}
                            {completeImagesItems()}
                        </Grid>
                        {submitted && (!assets || assets.length === 0) && (
                            <FormHelperText error>{t('common.components.profileImage.pickupImage')}</FormHelperText>
                        )}
                        <span className={classes.imagesRecommendation}>
                            {t('product.productFormConfiguration.recommendImage')}
                        </span>
                    </FormGroup>
                    <FormGroup className={classes.formGroup}>
                        <FormLabel>{t('product.productFormConfiguration.currency')}</FormLabel>
                        <CustomSelectValidator
                            name="currency"
                            select
                            fullWidth={true}
                            // className={classes.eventInputSelector}
                            value={fields.currency}
                            onChange={handleProductCurrencyFieldChange}
                            // InputLabelProps={{ shrink: false }}
                            validators={['required']}
                            errorMessages={[t('product.productFormConfiguration.currencyRequired')]}
                            SelectProps={{
                                MenuProps: {
                                    className: classes.menu,
                                },
                            }}
                            variant="outlined"
                            size="small"
                            InputProps={{
                                readOnly: readOnly,
                            }}
                        >
                            <MenuItem value=" " disabled>
                                <em>{t('product.productFormConfiguration.selectCurrency')}</em>
                            </MenuItem>
                            {currencies.map((currency) => {
                                return (
                                    <MenuItem key={currency.value} value={currency.value}>
                                        {`${currency.name} (${currency.value})`}
                                    </MenuItem>
                                );
                            })}
                        </CustomSelectValidator>
                    </FormGroup>
                    <Grid container spacing={1}>
                        <Grid item md={8}>
                            <FormGroup className={classes.formGroup}>
                                <FormLabel>{t('product.productFormConfiguration.discountType')}</FormLabel>
                                <CustomSelectValidator
                                    name="discountType"
                                    fullWidth={true}
                                    select
                                    // className={classes.eventInputSelector}
                                    value={fields.discountType}
                                    onChange={handleFieldChange}
                                    // InputLabelProps={{ shrink: false }}
                                    SelectProps={{
                                        MenuProps: {
                                            className: classes.menu,
                                        },
                                    }}
                                    InputProps={{
                                        readOnly: readOnly,
                                    }}
                                    variant="outlined"
                                    size="small"
                                >
                                    <MenuItem value=" " disabled>
                                        <em>{t('product.productFormConfiguration.selectDiscount')}</em>
                                    </MenuItem>
                                    {discounts.map((discount) => {
                                        return (
                                            <MenuItem key={discount.value} value={discount.value}>
                                                {t(discount.name)}
                                            </MenuItem>
                                        );
                                    })}
                                </CustomSelectValidator>
                            </FormGroup>
                        </Grid>
                        <Grid item md={4}>
                            <FormGroup className={classes.formGroup}>
                                <FormLabel>{t('product.productFormConfiguration.discount')}</FormLabel>
                                <CustomTextValidator
                                    name="discount"
                                    fullWidth={true}
                                    disabled={fields.discountType === 'none'}
                                    InputProps={{
                                        readOnly: readOnly,
                                        inputProps: {
                                            min: 0,
                                            decimalScale: currenciesHash[fields.currency].decimalScale,
                                        },
                                        inputComponent: NumberFormatCustom,
                                        startAdornment: (
                                            <span style={{ marginRight: '2px' }}>
                                                {' '}
                                                {fields.discountType === 'amount'
                                                    ? getSymbolFromCurrency(fields.currency) + ' '
                                                    : ''}
                                            </span>
                                        ),
                                        endAdornment: <span>{fields.discountType === 'percent' ? '%' : ''}</span>,
                                    }}
                                    value={fields.discountType !== 'none' ? fields.discount : undefined}
                                    onChange={handleFieldChange}
                                    variant="outlined"
                                    size="small"
                                    placeholder={'0'}
                                    validators={fields.discountType === 'none' ? [] : ['required']}
                                    errorMessages={[t('product.productFormConfiguration.discountRequired')]}
                                />
                            </FormGroup>
                        </Grid>
                    </Grid>

                    {!fields.enableVariants && (
                        <ProductFormCommon
                            decimalScale={currenciesHash[fields.currency].decimalScale}
                            readOnly={readOnly}
                            fields={productVariantFields}
                            handleFieldChange={handleProductVariantFieldChange}
                        />
                    )}

                    {canEdit ? (
                        <Stack direction="column" mt={2}>
                            <RenderFormExtraFields
                                fields={fields}
                                handleFieldChange={handleFieldChange}
                                name="extraFields"
                                formConfig={formConfigProduct}
                            />
                        </Stack>
                    ) : null}

                    <FormGroup className={classes.formGroup}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    disabled={readOnly}
                                    name="enableVariants"
                                    checked={fields.enableVariants}
                                    onChange={handleFieldChange}
                                    color="primary"
                                />
                            }
                            label={t('product.productFormConfiguration.multipleOptions')}
                        />
                    </FormGroup>
                </div>
            </div>
            <div className={classes.formButtons}>
                <div className={classes.bottomContent}>
                    <div className={classes.actions}>
                        <Button
                            variant="contained"
                            size="small"
                            disableElevation={true}
                            className={classes.cancelButton}
                            onClick={cancel}
                            disabled={savePending}
                        >
                            <span>
                                {readOnly
                                    ? t('product.productForm.close')
                                    : t('product.productFormConfiguration.cancel')}
                            </span>
                        </Button>
                        {(!readOnly || (readOnly && fields.enableVariants)) && (
                            <ActionButton
                                id="onlive-button-cart-shipping-save"
                                type="submit"
                                disableElevation={true}
                                className={classes.proceedButton}
                                disabled={savePending || sunError}
                                spinner={savePending}
                            >
                                <span>{getButtonText()}</span>
                            </ActionButton>
                        )}
                    </div>
                </div>
            </div>
            {/* <FormTip text="The quick, brown fox jumps over a lazy dog. DJs flock by when MTV ax quiz prog." /> */}
        </ValidatorForm>
    );
};

export default ProductFormConfiguration;
